import { useCallback, useEffect, useState } from 'react';
import { usePermissions } from '@/shared/platform';
import {
  NETWORK_DENTISTS,
  NETWORK_PAYERS,
  getNetworkCell,
  getNetworkChanges,
  networkSummary,
  setNetworkStatus,
  unconfirmedCount,
  useNetworksVersion,
  type NetworkCell,
  type NetworkDentist,
  type NetworkPayer,
  type NetworkStatus,
} from '@/features/networks/store';
import { Pill, SectionHeader, type PillKind } from './primitives';
import { Dialog, Toasts, useToasts } from './settingsInteractions';
import { PMS } from '@/features/shared/pms';

/**
 * Settings › Practice details › 03 Networks (D196, H4). Payer x dentist matrix
 * read from the shared networks store. Owners and admins change a cell in the
 * standard 460px dialog (D139, D142); everyone else reads it. Mock only.
 */

/** The acting user for the change log (mock; the real one comes from the session). */
const ACTING_USER = 'Ava Thompson (admin)';
const ACTING_ORG = 'Lone Star Billing';
const TODAY_ISO = '2026-10-21';

/** Payers by number of patients, most first (D168). Mock order. */
const PAYERS_BY_PATIENTS: readonly NetworkPayer[] = [
  'Delta Dental of Texas', 'Aetna', 'Cigna', 'MetLife', 'UnitedHealthcare',
  'BCBS of Texas', 'Guardian', 'Humana', 'Sun Life', 'United Concordia',
];
const PAYER_ROWS: readonly NetworkPayer[] = [
  ...PAYERS_BY_PATIENTS.filter((p) => NETWORK_PAYERS.includes(p)),
  ...NETWORK_PAYERS.filter((p) => !PAYERS_BY_PATIENTS.includes(p)),
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Mar 2019" -> "2019-03-01" for the date input; '' when it can't be read. */
function sinceToIso(since: string | undefined): string {
  const m = /^([A-Za-z]{3}) (\d{4})$/.exec(since ?? '');
  if (!m) return '';
  const idx = MONTHS.indexOf(m[1] ?? '');
  return idx < 0 ? '' : `${m[2] ?? ''}-${String(idx + 1).padStart(2, '0')}-01`;
}
/** "2019-03-14" -> "Mar 2019". */
function isoToSince(iso: string): string {
  const [y, mo] = iso.split('-');
  const idx = Number(mo) - 1;
  return `${MONTHS[idx] ?? ''} ${y ?? ''}`.trim();
}

function shortName(dentist: string): string {
  const parts = dentist.split(' ');
  return `Dr. ${parts[parts.length - 1] ?? dentist}`;
}

const STATUS_LABEL: Record<NetworkStatus, string> = { in: 'In network', out: 'Out of network', unconfirmed: 'Not confirmed' };
const STATUS_KIND: Record<NetworkStatus, PillKind> = { in: 'ok', out: 'mute', unconfirmed: 'warn' };

function metaLine(c: NetworkCell): string {
  if (c.status === 'in') {
    return `since ${c.since ?? ''}${c.feeSchedule ? ` · fee schedule ${c.feeSchedule}` : ''}`;
  }
  if (c.status === 'out') return 'no contract';
  return 'ELVA treats it as in network';
}

/** Fee schedules Dentrix has for this payer: the ones already attached to any dentist's plan. */
function feeSchedulesFor(payer: NetworkPayer): string[] {
  const names = new Set<string>();
  for (const d of NETWORK_DENTISTS) {
    const f = getNetworkCell(d, payer)?.feeSchedule;
    if (f) names.add(f);
  }
  return [...names];
}

export function useCanEditNetworks(): boolean {
  const { role } = usePermissions();
  // Owners and admins only (D139). Unknown role (demo/staging) is allowed.
  return role !== 'staff' && role !== 'provider';
}

// ── Dentists table cell ──

/** "In network with 9 payers · 1 not confirmed", linked to #networks. */
export function NetworksSummaryLink({ dentist }: { dentist: string }) {
  useNetworksVersion();
  const { inCount, unconfirmed } = networkSummary(dentist);
  const text = `In network with ${String(inCount)} payer${inCount === 1 ? '' : 's'}${
    unconfirmed > 0 ? ` · ${String(unconfirmed)} not confirmed` : ''
  }`;
  return (
    <a
      href="#networks"
      onClick={(e) => {
        e.preventDefault();
        document.getElementById('networks')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.replaceState(null, '', '#networks');
      }}
    >
      {text}
    </a>
  );
}

// ── Dialog ──

function NetworkDialog({
  dentist,
  payer,
  onClose,
  onSaved,
}: {
  dentist: NetworkDentist;
  payer: NetworkPayer;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const cell = getNetworkCell(dentist, payer);
  const status: NetworkStatus = cell?.status ?? 'in';
  const options = feeSchedulesFor(payer);
  const [mode, setMode] = useState<'in' | 'out'>(status === 'out' ? 'out' : 'in');
  const [date, setDate] = useState(status === 'in' ? sinceToIso(cell?.since) : '');
  const [fee, setFee] = useState(status === 'in' ? (cell?.feeSchedule ?? '') : '');

  const missing = mode === 'in' && (date === '' || fee === '');
  const who = shortName(dentist);
  const consequence =
    mode === 'out'
      ? `From today, ELVA posts ${payer} payments for ${who}'s patients with no write-off. Postings already made don't change.`
      : `From today, ELVA posts ${payer} payments for ${who}'s patients with the write-off from the fee schedule. Postings already made don't change.`;

  const save = () => {
    if (missing) return;
    if (mode === 'in') setNetworkStatus(dentist, payer, { status: 'in', since: isoToSince(date), feeSchedule: fee }, ACTING_USER, ACTING_ORG);
    else setNetworkStatus(dentist, payer, { status: 'out' }, ACTING_USER, ACTING_ORG);
    onSaved(`Saved. ${dentist} is now ${mode === 'in' ? 'in network' : 'out of network'} with ${payer}.`);
    onClose();
  };

  return (
    <Dialog
      title={`${dentist} and ${payer}`}
      help={
        <>
          Today: {STATUS_LABEL[status].toLowerCase()}.
          <br />
          <span className="setd-note">Where ELVA got it: {cell?.source ?? 'Not set'}.</span>
        </>
      }
      onClose={onClose}
      actions={
        <>
          {missing ? (
            <span id="net-why" className="setd-note" style={{ marginRight: 'auto', alignSelf: 'center' }}>
              Choose a date and a fee schedule to save.
            </span>
          ) : null}
          <button type="button" className="setd-btn setd-btn-quiet" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="setd-btn setd-btn-primary"
            disabled={missing}
            aria-describedby={missing ? 'net-why' : undefined}
            onClick={save}
          >
            Save
          </button>
        </>
      }
    >
      <div role="radiogroup" aria-label="Network" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div className="setd-opt">
          <input id="net-mode-in" type="radio" name="net-mode" checked={mode === 'in'} onChange={() => { setMode('in'); }} />
          <span>
            <label htmlFor="net-mode-in"><strong>In network since</strong></label>
            {mode === 'in' ? (
              <span className="setd-opt-fields">
                <label className="setd-field">
                  <span className="setd-field-label">Since</span>
                  <input
                    type="date"
                    className="setd-input"
                    style={{ width: 160 }}
                    value={date}
                    max={TODAY_ISO}
                    onChange={(e) => { setDate(e.target.value); }}
                  />
                </label>
                <label className="setd-field">
                  <span className="setd-field-label">Fee schedule</span>
                  <select className="setd-select" value={fee} onChange={(e) => { setFee(e.target.value); }}>
                    <option value="">Choose from {PMS}…</option>
                    {options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </label>
              </span>
            ) : null}
          </span>
        </div>
        <div className="setd-opt">
          <input id="net-mode-out" type="radio" name="net-mode" checked={mode === 'out'} onChange={() => { setMode('out'); }} />
          <span>
            <label htmlFor="net-mode-out"><strong>Out of network</strong></label>
            <span className="setd-note" style={{ display: 'block' }}>
              No contract. ELVA posts no write-off; the patient owes what {payer} doesn&rsquo;t pay.
            </span>
          </span>
        </div>
      </div>
      <div className="setd-quiet" style={{ marginTop: 14 }}>{consequence}</div>
    </Dialog>
  );
}

// ── Section ──

export function NetworksSection() {
  useNetworksVersion();
  const canEdit = useCanEditNetworks();
  const [editing, setEditing] = useState<{ dentist: NetworkDentist; payer: NetworkPayer } | null>(null);
  const { toasts, push } = useToasts();
  const close = useCallback(() => { setEditing(null); }, []);

  useEffect(() => {
    if (window.location.hash === '#networks') {
      document.getElementById('networks')?.scrollIntoView({ block: 'start' });
    }
  }, []);

  const unconfirmed = unconfirmedCount();
  const last = getNetworkChanges()[0];
  const lastLine = last
    ? `${last.by}${last.org ? `, ${last.org}` : ''}, ${last.at} (${last.dentist} and ${last.payer}: ${
        last.to === 'in' ? 'in network' : 'out of network'
      })`
    : 'Dr. Emily Carter (Owner), Oct 1';

  return (
    <section className="setd-card" id="networks">
      <SectionHeader n="03" title="Networks" />
      <p className="setd-desc">
        Who has a contract with which payer. ELVA uses it to decide the write-off on every posting. Out of network, there&rsquo;s
        no write-off and the patient owes what insurance doesn&rsquo;t pay.
      </p>
      {unconfirmed > 0 ? (
        <div className="setd-strip" style={{ marginBottom: 12 }}>
          <strong>{String(unconfirmed)} not confirmed.</strong> ELVA quotes {unconfirmed === 1 ? 'it' : 'them'} as in network, but holds
          postings that need a write-off until you confirm.
        </div>
      ) : null}
      <div style={{ overflowX: 'auto' }}>
        <table className="setd-tbl">
          <caption className="setd-sr">Network status by payer and dentist</caption>
          <thead>
            <tr>
              <th>Payer</th>
              {NETWORK_DENTISTS.map((d) => (
                <th key={d}>{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PAYER_ROWS.map((payer) => (
              <tr key={payer}>
                <th scope="row" className="rowh">{payer}</th>
                {NETWORK_DENTISTS.map((dentist) => {
                  const c = getNetworkCell(dentist, payer);
                  if (!c) return <td key={dentist} />;
                  const body = (
                    <>
                      <Pill kind={STATUS_KIND[c.status]}>{STATUS_LABEL[c.status]}</Pill>
                      <div className="sub">{metaLine(c)}</div>
                    </>
                  );
                  return (
                    <td key={dentist}>
                      {canEdit ? (
                        <button
                          type="button"
                          className="setd-cellbtn"
                          aria-label={`${dentist} and ${payer}: ${STATUS_LABEL[c.status].toLowerCase()}. Change`}
                          onClick={() => { setEditing({ dentist, payer }); }}
                        >
                          {body}
                        </button>
                      ) : (
                        <div>{body}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="setd-change">
        Read from the fee schedule attached to each plan in {PMS}, and confirmed with Dr. Carter at onboarding.{' '}
        {canEdit ? 'Only owners and admins can change these.' : 'Only owners and admins can change these, so this is read-only for you.'}{' '}
        Last change: {lastLine}.
      </div>
      {editing ? (
        <NetworkDialog dentist={editing.dentist} payer={editing.payer} onClose={close} onSaved={push} />
      ) : null}
      <Toasts toasts={toasts} />
    </section>
  );
}
